import { ClientEvent } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useState } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

interface UserSettingsContent {
  soundNotifications?: boolean;
  [key: string]: unknown;
}

const useSoundSettings = (): { isMuted: boolean; setMuted: (value: boolean) => Promise<void> } => {
  const { matrixClient } = useMatrixContext();

  const readIsMuted = useCallback((): boolean => {
    try {
      const data = matrixClient.getAccountData(CustomMeedioMatrixEvents.USER_SETTINGS as never);
      const content = data?.getContent<UserSettingsContent>();
      return content?.soundNotifications !== true;
    } catch (error) {
      logger.warn('useSoundSettings: failed to read account data', error);
      return true;
    }
  }, [matrixClient]);

  const [isMuted, setIsMutedState] = useState<boolean>(() => readIsMuted());

  useEffect(() => {
    const handler = (event: { getType: () => string }) => {
      if (event.getType() !== CustomMeedioMatrixEvents.USER_SETTINGS) return;
      setIsMutedState(readIsMuted());
    };

    matrixClient.on(ClientEvent.AccountData, handler);
    return () => {
      matrixClient.off(ClientEvent.AccountData, handler);
    };
  }, [matrixClient, readIsMuted]);

  const setMuted = useCallback(
    async (value: boolean) => {
      const existing = matrixClient.getAccountData(CustomMeedioMatrixEvents.USER_SETTINGS as never);
      const existingContent = (existing?.getContent<UserSettingsContent>() ?? {}) as UserSettingsContent;
      const merged: UserSettingsContent = { ...existingContent, soundNotifications: !value };
      await matrixClient.setAccountData(CustomMeedioMatrixEvents.USER_SETTINGS as never, merged as never);
      setIsMutedState(value);
    },
    [matrixClient]
  );

  return { isMuted, setMuted };
};

export default useSoundSettings;
